import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  getSaleEntries,
  addSaleEntry,
  removeSaleEntry,
  moveSaleEntry,
  reorderSaleEntries,
  setSaleEntryVisibleOnTop,
  updateSaleEntriesBulk,
  SaleEntryWithProduct,
} from '@/services/backToSchoolService';
import { searchProducts } from '@/services/haulService';
import { ProductItem } from '@/services/productsService';
import { FIND_SUBCATEGORIES } from '@/lib/findSubcategories';
import { cn } from '@/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  Package,
  GraduationCap,
  Loader2,
  Pin,
  PinOff,
  GripVertical,
  Wand2,
  X,
} from 'lucide-react';

const NO_CHANGE = '__no_change__';
type PinMode = 'none' | 'visible' | 'hidden';

const BackToSchoolList = () => {
  const [entries, setEntries] = useState<SaleEntryWithProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [togglingPinId, setTogglingPinId] = useState<number | null>(null);

  // Drag-and-drop reordering
  const [dragId, setDragId] = useState<number | null>(null);
  const [dragOverId, setDragOverId] = useState<number | null>(null);

  // Selection + small bulk edit state
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [pinMode, setPinMode] = useState<PinMode>('none');
  const [bulkSubcategory, setBulkSubcategory] = useState<string>(NO_CHANGE);
  const [bulkBusy, setBulkBusy] = useState(false);

  // Product search state (same pattern as the hauls form)
  const [pickerOpen, setPickerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ProductItem[]>([]);
  const [searchTotal, setSearchTotal] = useState(0);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchPage, setSearchPage] = useState(1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const PER_PAGE = 30;

  const fetchEntries = async () => {
    try {
      setLoading(true);
      const data = await getSaleEntries();
      setEntries(data);
    } catch (error) {
      console.error('Error fetching sale entries:', error);
      toast.error('Failed to load sale items. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, []);

  // Server-side product search with debounce
  const doSearch = useCallback(async (query: string, page: number, append: boolean) => {
    setSearchLoading(true);
    try {
      const { data, count } = await searchProducts(query, page, PER_PAGE);
      if (append) {
        setSearchResults((prev) => [...prev, ...(data || [])]);
      } else {
        setSearchResults(data || []);
      }
      setSearchTotal(count || 0);
    } catch (e) {
      console.error('Error searching products:', e);
      toast.error('Failed to search products');
      if (!append) setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // Debounced search when query changes
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSearchPage(1);
    debounceRef.current = setTimeout(() => {
      doSearch(searchQuery, 1, false);
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery, doSearch]);

  // Load initial results when the picker opens, reset when it closes
  useEffect(() => {
    if (pickerOpen) {
      if (searchResults.length === 0 && !searchLoading) {
        doSearch('', 1, false);
      }
    } else {
      setSearchQuery('');
      setSearchPage(1);
    }
  }, [pickerOpen]);

  const handleLoadMore = () => {
    const nextPage = searchPage + 1;
    setSearchPage(nextPage);
    doSearch(searchQuery, nextPage, true);
  };

  const hasMore = searchResults.length < searchTotal;

  // Filter out products already in the sale
  const displayedProducts = useMemo(() => {
    const saleIds = new Set(entries.map((e) => e.product_id));
    return searchResults.filter((p) => !saleIds.has(p._id));
  }, [searchResults, entries]);

  const handleAdd = async (p: ProductItem) => {
    setPickerOpen(false);
    try {
      setAdding(true);
      await addSaleEntry(p._id);
      toast.success(`"${p.name}" added to the sale`);
      await fetchEntries();
    } catch (err: any) {
      console.error('Error adding sale entry:', err);
      const message =
        err?.message?.includes('already in') || err?.code === '23505'
          ? 'This product is already in the sale'
          : err?.message || 'Failed to add product to sale';
      toast.error(message);
    } finally {
      setAdding(false);
    }
  };

  const handleMove = async (entry: SaleEntryWithProduct, direction: 'up' | 'down') => {
    if (busy) return;
    try {
      setBusy(true);
      await moveSaleEntry(entry.id, direction);
      await fetchEntries();
    } catch (err: any) {
      console.error('Error reordering sale entry:', err);
      toast.error(err?.message || 'Failed to reorder');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (entry: SaleEntryWithProduct) => {
    try {
      setDeletingId(entry.id);
      await removeSaleEntry(entry.id);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(entry.product_id);
        return next;
      });
      toast.success('Product removed from the sale');
      await fetchEntries();
    } catch (err: any) {
      console.error('Error removing sale entry:', err);
      toast.error(err?.message || 'Failed to remove product from sale');
    } finally {
      setDeletingId(null);
    }
  };

  // Per-row "visible on top" toggle
  const handleTogglePin = async (entry: SaleEntryWithProduct) => {
    try {
      setTogglingPinId(entry.id);
      await setSaleEntryVisibleOnTop(entry.id, !entry.visible_on_top);
      setEntries((prev) =>
        prev.map((e) =>
          e.id === entry.id ? { ...e, visible_on_top: !entry.visible_on_top } : e,
        ),
      );
      toast.success(
        !entry.visible_on_top
          ? `"${entry.product?.name ?? `#${entry.product_id}`}" pinned to top`
          : `"${entry.product?.name ?? `#${entry.product_id}`}" unpinned`,
      );
    } catch (err: any) {
      console.error('Error toggling visibility:', err);
      toast.error(err?.message || 'Failed to update visibility');
    } finally {
      setTogglingPinId(null);
    }
  };

  // --- Drag-and-drop reordering ---
  const handleDragStart = (entry: SaleEntryWithProduct) => {
    if (busy) return;
    setDragId(entry.id);
  };

  const handleDragOver = (e: React.DragEvent, entry: SaleEntryWithProduct) => {
    e.preventDefault();
    if (dragId === null || entry.id === dragId) return;
    setDragOverId(entry.id);
  };

  const resetDragState = () => {
    setDragId(null);
    setDragOverId(null);
  };

  const handleDrop = async (target: SaleEntryWithProduct) => {
    const sourceId = dragId;
    resetDragState();
    if (sourceId === null || sourceId === target.id) return;

    const fromIndex = entries.findIndex((e) => e.id === sourceId);
    const toIndex = entries.findIndex((e) => e.id === target.id);
    if (fromIndex === -1 || toIndex === -1) return;

    // Reorder locally (optimistic) then persist.
    const next = [...entries];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    setEntries(next);

    try {
      setBusy(true);
      await reorderSaleEntries(next.map((e) => e.id));
      toast.success('Order updated');
    } catch (err: any) {
      console.error('Error reordering sale entries:', err);
      toast.error(err?.message || 'Failed to save the new order');
      await fetchEntries();
    } finally {
      setBusy(false);
    }
  };

  // --- Selection helpers ---
  const allSelected = entries.length > 0 && selectedIds.size === entries.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  const toggleSelect = (productId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) =>
      prev.size === entries.length ? new Set() : new Set(entries.map((e) => e.product_id)),
    );
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setPinMode('none');
    setBulkSubcategory(NO_CHANGE);
  };

  const canApplyBulk = selectedIds.size > 0 && (pinMode !== 'none' || bulkSubcategory !== NO_CHANGE);

  const handleApplyBulk = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    const updates: { visibleOnTop?: boolean; findSubcategory?: string } = {};
    if (pinMode === 'visible') updates.visibleOnTop = true;
    if (pinMode === 'hidden') updates.visibleOnTop = false;
    if (bulkSubcategory !== NO_CHANGE) updates.findSubcategory = bulkSubcategory;

    try {
      setBulkBusy(true);
      await updateSaleEntriesBulk(ids, updates);

      const parts: string[] = [];
      if (updates.visibleOnTop !== undefined) {
        parts.push(updates.visibleOnTop ? 'pinned to top' : 'unpinned');
      }
      if (updates.findSubcategory !== undefined) {
        parts.push(`subcategory “${updates.findSubcategory}”`);
      }
      toast.success(`Updated ${ids.length} item(s): ${parts.join(' · ')}`);

      await fetchEntries();
      clearSelection();
    } catch (err: any) {
      console.error('Error applying bulk update:', err);
      toast.error(err?.message || 'Failed to apply the bulk update');
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Back 2 School Sale</h1>
          <p className="text-muted-foreground">
            Manage products featured on the Back 2 School sale page. The first row appears first on the site.
          </p>
        </div>
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <Button disabled={adding}>
              {adding ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Plus className="mr-2 h-4 w-4" />
              )}
              Add Product
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[500px] p-0" align="end">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Search by product name or ID (e.g. 4694)..."
                value={searchQuery}
                onValueChange={setSearchQuery}
              />
              <CommandList className="max-h-[300px] overflow-y-auto">
                {searchLoading && displayedProducts.length === 0 ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    <span className="ml-2 text-sm text-muted-foreground">Searching...</span>
                  </div>
                ) : displayedProducts.length === 0 ? (
                  <CommandEmpty>No products found.</CommandEmpty>
                ) : (
                  <CommandGroup>
                    {displayedProducts.map((p) => (
                      <CommandItem key={p._id} onSelect={() => handleAdd(p)}>
                        <div className="flex flex-1 items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            {p.image ? (
                              <img
                                src={p.image}
                                alt={p.name}
                                className="w-8 h-8 rounded object-cover shrink-0"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.visibility = 'hidden';
                                }}
                              />
                            ) : null}
                            <span className="truncate">{p.name}</span>
                          </div>
                          <span className="text-muted-foreground text-xs ml-2 shrink-0">
                            #{p._id} · ${p.price}
                          </span>
                        </div>
                      </CommandItem>
                    ))}
                    {hasMore && (
                      <CommandItem
                        onSelect={handleLoadMore}
                        className="justify-center text-primary hover:text-primary-foreground hover:bg-primary cursor-pointer"
                      >
                        {searchLoading ? (
                          <span className="flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading...
                          </span>
                        ) : (
                          `Load more (${searchTotal - searchResults.length} remaining)`
                        )}
                      </CommandItem>
                    )}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* Small bulk edit */}
      {entries.length > 0 && (
        <Card className="bg-background border border-border/50 shadow-subtle">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Bulk edit</CardTitle>
            <CardDescription>
              {selectedIds.size === 0
                ? 'Select items below to pin them to the top or set their Find Subcategory.'
                : `${selectedIds.size} item(s) selected. Only the fields you change are written.`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col lg:flex-row lg:items-end gap-4">
              <div className="space-y-2">
                <Label>Visible on top</Label>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant={pinMode === 'visible' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setPinMode((prev) => (prev === 'visible' ? 'none' : 'visible'))}
                  >
                    <Pin className="mr-2 h-4 w-4" />
                    Pin to top
                  </Button>
                  <Button
                    type="button"
                    variant={pinMode === 'hidden' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setPinMode((prev) => (prev === 'hidden' ? 'none' : 'hidden'))}
                  >
                    <PinOff className="mr-2 h-4 w-4" />
                    Unpin
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bulk-subcategory">Find Subcategory</Label>
                <Select value={bulkSubcategory} onValueChange={setBulkSubcategory}>
                  <SelectTrigger id="bulk-subcategory" className="w-[240px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_CHANGE}>— Keep current subcategory —</SelectItem>
                    {FIND_SUBCATEGORIES.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-wrap items-center gap-2 lg:ml-auto">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={selectedIds.size === 0 || bulkBusy}
                  onClick={clearSelection}
                >
                  <X className="mr-2 h-4 w-4" />
                  Clear selection
                </Button>
                <Button
                  type="button"
                  size="sm"
                  disabled={!canApplyBulk || bulkBusy}
                  onClick={handleApplyBulk}
                >
                  {bulkBusy ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Wand2 className="mr-2 h-4 w-4" />
                  )}
                  Apply to {selectedIds.size}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="bg-background border border-border/50 shadow-subtle">
        <CardHeader className="pb-2">
          <CardTitle>Sale items</CardTitle>
          <CardDescription>
            {loading ? 'Loading...' : `${entries.length} product(s) in the sale`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
                <span className="text-sm text-muted-foreground">Loading sale items...</span>
              </div>
            </div>
          ) : entries.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
              <div className="w-16 h-16 rounded-full bg-muted/30 flex items-center justify-center">
                <GraduationCap className="h-8 w-8 text-muted-foreground" />
              </div>
              <div className="text-center">
                <h3 className="text-lg font-medium">No sale items yet</h3>
                <p className="text-muted-foreground">Add products to feature them on the Back 2 School page</p>
              </div>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8" />
                    <TableHead className="w-10">
                      <Checkbox
                        checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                        onCheckedChange={toggleSelectAll}
                        aria-label="Select all"
                      />
                    </TableHead>
                    <TableHead className="w-16 text-center">Order</TableHead>
                    <TableHead className="w-16">Thumbnail</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Details</TableHead>
                    <TableHead className="w-24 text-center">On Top</TableHead>
                    <TableHead className="w-24">Price</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((entry, index) => (
                    <TableRow
                      key={entry.id}
                      onDragOver={(e) => handleDragOver(e, entry)}
                      onDrop={() => handleDrop(entry)}
                      onDragEnd={resetDragState}
                      className={cn(
                        'transition-colors',
                        dragId === entry.id && 'opacity-50',
                        dragOverId === entry.id &&
                          dragId !== null &&
                          dragId !== entry.id &&
                          'bg-primary/10 border-t-2 border-t-primary',
                      )}
                    >
                      <TableCell className="w-8 px-1">
                        {/* Drag handle — grab this small area to reorder */}
                        <div
                          draggable={!busy}
                          onDragStart={() => handleDragStart(entry)}
                          className={cn(
                            'flex h-8 w-6 cursor-grab items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground active:cursor-grabbing',
                            busy && 'cursor-not-allowed opacity-50',
                          )}
                          title="Drag to reorder"
                          aria-label="Drag to reorder"
                        >
                          <GripVertical className="h-4 w-4" />
                        </div>
                      </TableCell>
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.has(entry.product_id)}
                          onCheckedChange={() => toggleSelect(entry.product_id)}
                          aria-label={`Select product #${entry.product_id}`}
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span className="text-xs font-semibold text-muted-foreground">
                            #{index + 1}
                          </span>
                          <div className="flex gap-0.5">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              disabled={busy || index === 0}
                              onClick={() => handleMove(entry, 'up')}
                              title="Move up"
                            >
                              <ArrowUp className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              disabled={busy || index === entries.length - 1}
                              onClick={() => handleMove(entry, 'down')}
                              title="Move down"
                            >
                              <ArrowDown className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="w-12 h-12 rounded-md bg-muted overflow-hidden">
                          {entry.product?.image ? (
                            <img
                              src={entry.product.image}
                              alt={entry.product.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = '/placeholder.svg';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="h-5 w-5 text-muted-foreground" />
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {entry.product ? (
                          <p className="font-medium">{entry.product.name}</p>
                        ) : (
                          <p className="font-medium text-destructive">
                            Missing product #{entry.product_id}
                          </p>
                        )}
                      </TableCell>
                      <TableCell>
                        {entry.product ? (
                          <div className="space-y-1">
                            <p className="text-muted-foreground text-sm">
                              {entry.product.brand || '—'}
                              {entry.product.category ? ` · ${entry.product.category}` : ''}
                            </p>
                            <Badge variant="outline" className="bg-background/50">
                              {entry.product.find_subcategory || 'None'}
                            </Badge>
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">
                            Product no longer exists — remove this row
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant={entry.visible_on_top ? 'default' : 'outline'}
                          size="sm"
                          className="h-8"
                          disabled={togglingPinId === entry.id}
                          onClick={() => handleTogglePin(entry)}
                          title={entry.visible_on_top ? 'Unpin from top' : 'Pin to top'}
                        >
                          {togglingPinId === entry.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : entry.visible_on_top ? (
                            <Pin className="h-3.5 w-3.5" />
                          ) : (
                            <PinOff className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      </TableCell>
                      <TableCell>
                        {entry.product ? (
                          <span className="text-sm font-medium">${entry.product.price}</span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive"
                              disabled={deletingId === entry.id}
                            >
                              {deletingId === entry.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-background border border-border/50">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will remove "
                                {entry.product?.name || `Product #${entry.product_id}`}" from the
                                Back 2 School sale. This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="bg-background hover:bg-muted">
                                Cancel
                              </AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-destructive text-white hover:bg-destructive/90"
                                onClick={() => handleDelete(entry)}
                              >
                                Remove
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BackToSchoolList;